import { inject } from '@angular/core';
import { CanActivateChildFn, Router } from '@angular/router';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';
import { Auth } from '../auth/auth';
import { PolicyAcknowledgementGateService } from '../services/policy-acknowledgement-gate.service';

// Policy assignments only ever resolve against an EmployeeProfile row
// (myPoliciesController.js / resolveMyPolicies), which admin, sub_admin,
// super_admin and client logins never have - the BE already returns an
// empty list for them, but only after a round-trip to ess-service on every
// single navigation. That made a non-employee session's entire app
// depend on ess-service being up, and (before the ess-service crash fix)
// meant an unrelated service outage locked admins/super-admins out of
// every page too, not just employees. Skip the check outright for any
// role outside the employee identity space - mirrors the same
// employee-vs-admin split header.ts already uses for force-logout targeting.
const EMPLOYEE_SPACE_ROLE_TYPES = ['employee', 'super_user'];

export const policyAcknowledgementGuard: CanActivateChildFn = () => {
  const router = inject(Router);
  const auth = inject(Auth);
  const gate = inject(PolicyAcknowledgementGateService);

  const user = auth.currentUser();
  const roleType = user?.role_type;
  if (!roleType || !EMPLOYEE_SPACE_ROLE_TYPES.includes(roleType)) {
    return of(true);
  }
  // Bug 21574: an Admin user group (ESS + Admin login, switched to Admin or
  // signed in with it) is still an employee-space session - it reports
  // role_type 'super_user' - but policy acknowledgement belongs to the ESS
  // context only, so it must not be blocked here.
  if (user?.is_admin_group === true) {
    return of(true);
  }

  return gate.ensureLoaded().pipe(
    map((hasPending) => (hasPending ? router.createUrlTree(['/policy-acknowledgement']) : true)),
  );
};
